/**
 * @fileOverview ViewStore for base date picker view using React component.
 * @author jzetlen
 *
 */

import { extend, partial } from 'underscore';
import moment from 'moment';
import invariant from 'invariant-slim';
import presets from 'widgets/datepickerPresets';
import trackLink from 'view/activity/trackLink';

/**
 * @function datePickerStore
 * Factory for a base date picker store. This is a View Store function that
 * follows the convention `f => f => state`. The outermost function takes
 * immutable configuration values, such as a global minDate, maxDate, labels,
 * and other values that should never change. it holds this config object
 * in the closure and returns a function that is a factory for initial state.
 */
export default function datePickerStore(config) {
	// this outer closure is for utilities and state that will be common
	// to all stores, similar to static properties on a class.
	const { labels, formats, minDate, maxDate, startOfWeek } = config;

	// validate config shape
	invariant(
		minDate.getTime && !isNaN(minDate.getTime()),
		'minDate must be a valid date'
	);
	invariant(
		maxDate.getTime && !isNaN(maxDate.getTime()),
		'maxDate must be a valid date'
	);
	invariant(typeof labels === 'object', 'labels required');
	invariant(typeof formats === 'object', 'formats required');
	invariant(typeof formats.date === 'string', 'formats.date required');
	invariant(
		typeof formats.monthHeader === 'string',
		'formats.monthHeader required'
	);

	// construct base
	// all state objects have a startDate and an endDate property; this is
	// the initial two values that will be added to state
	const defaultDates = getDefaultDateRange();

	// initialize moment's default locale('en') with correct localized months from Griffin
	// adjust long date format to only show month example: ru_RU
	let months = [...labels['month-name']];
	moment.locale('en', {
		months: months,
		longDateFormat: {
			LLL: 'MMMM'
		}
	});

	/**
	 * Factory for date picker store initial state. This inner function has
	 * a reference to the initial immutable `config` above, but it receives
	 * the live store as its argument. therefore, it has access to current
	 * store state via `getState`, and can update and trigger events with
	 * `updateState` and `publish`. it should return an object that will be
	 * used as initial state; since the React components this store controls
	 * take all their data and handlers as props, this mixes initial data
	 * and the functionality that consumes it together. it also provides
	 * two separate closures with which to hold private state per store.
	 */
	return function({ getState, updateState, onPublish }) {
		// this inner closure is for state that will be private to the
		// individual store created with each invocation of this function,
		// similar to private instance properties on a class.

		// track FPTI events on publish, and update the picker with the
		// published dates, as well as signalling that editing has concluded
		onPublish(function onPublishDates (event, startDate, endDate) {
			const pickerState = getState();
			const isStartDateSame = moment(startDate).isSame(pickerState.startDate, 'day');
			const isEndDateSame = moment(endDate).isSame(pickerState.endDate, 'day');

			if (!isStartDateSame) {
				trackLink('opencalendar:start:');
			}
			if (!isEndDateSame) {
				trackLink('opencalendar:end:');
			}
			if (!isStartDateSame || !isEndDateSame) {
				updateState({
					startDate,
					endDate,
					editing: false
				});
			}
		});
		// pass labels, formatters, validators to all components
		const base = {
			labels,
			rtl: config.rtl,
			startOfWeek,
			formatDate: partial(formatDate, formats.date),
			formatMonthHeader: partial(formatDate, formats.monthHeader),
			validateDate: partial(isWithinRange, { minDate, maxDate }),
			// sensible default for the initial month to show, but this
			// will change as the user navigates
			showMonth: defaultDates.endDate
		};
		return extend(
			// CSS class names to be passed to components
			getDatePickerClassNames(),
			// { startDate, endDate } object declared above
			defaultDates,
			base
		);
	};
}

export function getDefaultDateRange () {
	// always start with the last 90 days. TODO: store this somewhere per user
	const [startDate, endDate] = presets.last90();
	return { startDate, endDate };
}

export const formatDate = (format, date) => moment(date).format(format);

export const isWithinRange = ({ minDate, maxDate }, date) => {
	// the '[]' indicates inclusive on both sides, per Moment docs
	return moment(date).isBetween(minDate, maxDate, 'day', '[]');
};

export const getDatePickerClassNames = () => ({
	className: 'vx_form-group customDates',
	inputClassName: 'vx_form-control vx_form-control_complex form-control',
	popoverClassName: 'nemo_datePicker',
	closeButtonClassName: 'nemo_datepickerClose',
	monthHeaderClassName: 'nemo_datepickerMonthHeader',
	prevLinkClassName: 'nemo_datepickerPreviousMonth',
	nextLinkClassName: 'nemo_datepickerNextMonth',
	dayClassName: 'nemo_datepickerDay',
	nameSpace: 'customDates'
});




// WEBPACK FOOTER //
// ./view/activity/dateRangePicker/stores/base.es6